<template>
  <div>
    <div class="tw-mb-1">
      <div class="tw-flex tw-items-center">
        <label v-if="label" class="tw-block tw-text-xs dark:tw-text-white">
          {{ label }}
        </label>
        <slot name="info" />
      </div>
      <div v-if="subLabel" class="tw-mt-1 tw-text-xs tw-text-slate-500">
        {{ subLabel }}
      </div>
    </div>
    <div
      :id="id"
      ref="selectedOption"
      class="studio-advanced-select tw-mt-1 tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-border tw-bg-white tw-px-3 tw-py-2 tw-text-base dark:tw-bg-black sm:tw-text-sm"
      :class="{
        'tw-cursor-pointer hover:tw-border hover:tw-border-deep-orange-500': !disabled && !isDropdownOpen,
        'tw-cursor-not-allowed': disabled,
        'tw-border-transparent tw-ring-2 tw-ring-deep-orange-500': isDropdownOpen
      }"
      :data-testid="id"
      @click="toggleDropdown"
    >
      <div v-if="!selectedOption || !selectedOption.value" class="tw-truncate tw-pr-[12px]">
        <div class="tw-text-s tw-block tw-truncate tw-text-black dark:tw-text-white">{{ placeholder }}</div>
        <div v-if="!hideSubtext" class="tw-block tw-truncate tw-text-xs tw-text-slate-500">Click to open</div>
      </div>
      <div v-else class="tw-max-w-xs tw-truncate tw-pr-[12px]">
        <div class="tw-flex tw-items-center tw-gap-2">
          <p
            id="selected-label"
            class="tw-text-s tw-block tw-truncate tw-text-black dark:tw-text-white"
            :class="{
              '!tw-text-slate-500': disabled
            }"
          >
            {{ selectedOption && selectedOption.label }}
          </p>
          <div
            v-if="selectedOption?.badges?.length"
            class="tw-ml-auto tw-flex tw-max-w-[200px] tw-items-center tw-gap-2"
          >
            <StudioBadge
              v-for="badge in selectedOption.badges"
              :key="badge.label"
              :title="badge.label"
              :variation="badge.variation"
            >
              <component :is="badge.icon" class="tw-mr-1 tw-w-4" />
              <p class="tw-truncate">
                {{ badge.label }}
              </p>
            </StudioBadge>
          </div>
        </div>
        <div v-if="!hideSubtext" id="selected-value" class="tw-block tw-truncate tw-text-xs tw-text-slate-500">
          {{ selectedOption && getOptionSubLabel(selectedOption) }}
        </div>
      </div>
    </div>
    <dialog ref="dialog">
      <div
        v-if="isDropdownOpen"
        id="options-dropdown"
        ref="optionsDropdown"
        v-studio-click-outside="closeDropdown"
        class="tw-fixed tw-z-50 tw-mt-1 tw-flex tw-max-h-[300px] tw-flex-col tw-overflow-y-auto tw-rounded tw-border tw-border-slate-100 tw-bg-white tw-pb-1 tw-text-sm tw-shadow-sm dark:tw-border-slate-700 dark:tw-bg-black"
        :data-testid="id + '-options'"
      >
        <div class="tw-sticky tw-top-0 tw-z-50 tw-w-full tw-bg-white tw-px-3 tw-pb-2 tw-pt-3 dark:tw-bg-black">
          <StudioSearchInput id="search-input" :searchTerm="searchTerm" alwaysExpanded @onSearch="onSearch" />
        </div>
        <div v-if="isLoadingData" class="tw-flex tw-h-full tw-items-center tw-justify-center tw-py-4">
          <StudioSpinner size="small" />
        </div>

        <div v-else-if="filteredOptions.length === 0" class="tw-h-full">
          <slot v-if="$slots['empty-state']" name="empty-state" />
          <div v-else class="tw-grid tw-h-full tw-place-items-center tw-text-xs tw-text-slate-500">
            No options found!
          </div>
        </div>

        <div
          v-for="option in filteredOptions"
          v-else
          :id="`item-${option.value}`"
          :key="option.value"
          class="option tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-rounded tw-px-3 tw-py-2 tw-text-sm hover:tw-bg-slate-100 hover:tw-text-deep-orange-500"
          :class="{
            '!tw-cursor-not-allowed': option.disabled,
            'tw-bg-slate-100 dark:tw-bg-slate-900': option.value === (selectedOption && selectedOption.value)
          }"
          @click="onSelectOption(option)"
        >
          <ThemeMoodBoard
            v-if="typeof option.previewImage === 'object'"
            class="tw-mr-2 tw-w-20"
            :themeObj="option.previewImage"
          />
          <div class="tw-flex tw-max-w-xs tw-flex-col tw-gap-1 tw-overflow-x-hidden">
            <div
              class="tw-block tw-truncate tw-text-xs hover:tw-whitespace-normal"
              :class="{
                'tw-text-deep-orange-500': option.value === (selectedOption && selectedOption.value),
                '!tw-text-slate-500': option.value !== (selectedOption && selectedOption.value) && option.disabled
              }"
            >
              {{ option.label }}
            </div>
            <div v-if="!hideSubtext" class="tw-block tw-max-w-[200px] tw-truncate tw-text-xs tw-text-slate-500">
              {{ getOptionSubLabel(option) }}
            </div>
          </div>
          <StudioBadge
            v-if="option.badgeLabel"
            :title="option.badgeLabel"
            variation="gray"
            class="tw-ml-auto tw-max-w-[200px]"
          >
            <p class="tw-truncate">
              {{ option.badgeLabel }}
            </p>
          </StudioBadge>
          <div v-if="option?.badges?.length" class="tw-ml-auto tw-flex tw-max-w-[200px] tw-items-center tw-gap-2">
            <StudioBadge
              v-for="badge in option.badges"
              :key="badge.label"
              :title="badge.label"
              :variation="badge.variation"
            >
              <component :is="badge.icon" class="tw-mr-1 tw-w-4" />
              <p class="tw-truncate">
                {{ badge.label }}
              </p>
            </StudioBadge>
          </div>
        </div>
        <div v-if="showLoadMore" class="tw-mx-auto tw-cursor-pointer" @click="() => $emit('loadMore')">Load More</div>
      </div>
    </dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, nextTick } from 'vue';

import StudioBadge, { BadgeVariants } from './StudioBadge.vue';
import ThemeMoodBoard from './ThemeMoodBoard.vue';

import { ThemeObj } from '@/studio/studioEventList/store/types';
import StudioSearchInput from '@/studioComponents/StudioSearchInput.vue';
import StudioSpinner from '@/studioComponents/StudioSpinner.vue';

export type StudioAdvancedSelectOption = {
  label: string;
  value: string;
  disabled?: boolean;
  subLabel?: string;
  previewImage?: ThemeObj | string;
  badgeLabel?: string;
  badges?: Array<{ label: string; variation: BadgeVariants; icon?: any }>;
};

export default defineComponent({
  name: 'StudioAdvancedSelect',
  components: { StudioSearchInput, StudioSpinner, ThemeMoodBoard, StudioBadge },
  props: {
    options: {
      type: Array as PropType<StudioAdvancedSelectOption[]>,
      required: true
    },
    selectedOption: {
      type: Object as PropType<StudioAdvancedSelectOption | null>,
      default: null
    },
    hideSubtext: { type: Boolean, default: false },
    label: { type: String, default: '' },
    subLabel: { type: String, default: '' },
    id: { type: String, required: true },
    disabled: { type: Boolean, default: false },
    isLoadingData: {
      type: Boolean,
      default: false
    },
    showOptionValueAsSubLabel: {
      type: Boolean,
      default: true
    },
    placeholder: {
      type: String,
      default: 'Select an option'
    },
    showLoadMore: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isDropdownOpen: false as boolean,
      searchTerm: '' as string
    };
  },
  computed: {
    filteredOptions(): StudioAdvancedSelectOption[] {
      if (!this.searchTerm) {
        return this.options;
      }
      const lowerCasedSearchTerm = this.searchTerm.toLowerCase();
      return this.options.filter(
        option =>
          option.label.toLowerCase().includes(lowerCasedSearchTerm) ||
          option.value.toLowerCase().includes(lowerCasedSearchTerm)
      );
    }
  },
  watch: {
    isDropdownOpen() {
      if (this.isDropdownOpen) {
        nextTick(() => {
          const dialog = this.$refs.dialog as any;
          if (dialog?.showModal) {
            dialog.showModal();
          }
          this.setDropdownPosition();
          this.setDropdownWidth();
          this.scrollToSelectedOption();
        });
      }
    }
  },
  methods: {
    toggleDropdown() {
      if (this.disabled) {
        return;
      }
      if (!this.isDropdownOpen) {
        this.isDropdownOpen = true;
      } else {
        this.closeDropdown();
      }
    },
    closeDropdown() {
      this.isDropdownOpen = false;
      const dialog = this.$refs.dialog as any;
      if (dialog?.close) {
        dialog.close();
      }
      this.searchTerm = '';
    },
    onSelectOption(selectedOption: StudioAdvancedSelectOption) {
      if (selectedOption.disabled) {
        return;
      }
      this.$emit('onOptionSelect', selectedOption);
      this.closeDropdown();
    },
    onSearch(newSearchTerm) {
      this.searchTerm = newSearchTerm;
      this.$emit('apiCallSearch', newSearchTerm);
    },
    setDropdownWidth() {
      const selectedOptionEl: HTMLDivElement = this.$refs.selectedOption as HTMLDivElement;
      const optionsDropdownEl: HTMLDivElement = this.$refs.optionsDropdown as HTMLDivElement;
      optionsDropdownEl.style.width = `${String(selectedOptionEl.offsetWidth)}px`;
    },
    scrollToSelectedOption() {
      setTimeout(() => {
        if (!this.selectedOption) {
          return;
        }

        const optionsDropdownEl: HTMLDivElement = this.$refs.optionsDropdown as HTMLDivElement;
        const selectedOptionEl = document.getElementById(`item-${this.selectedOption.value}`);

        if (optionsDropdownEl && selectedOptionEl) {
          optionsDropdownEl.scrollTo({
            top: selectedOptionEl.offsetTop - 68,
            behavior: 'auto'
          });
        }
      }, 100);
    },
    setDropdownPosition() {
      const inputEl: HTMLDivElement = this.$refs.selectedOption as HTMLDivElement;
      const optionsDropdownEl: HTMLDivElement = this.$refs.optionsDropdown as HTMLDivElement;
      const inputElPosition = inputEl.getBoundingClientRect();
      if (optionsDropdownEl) {
        const inputElHeight = inputEl.offsetHeight;
        const optionsDropdownElHeight = optionsDropdownEl.offsetHeight;
        optionsDropdownEl.style.left = `${inputElPosition.left}px`;
        const availableHeight = window.innerHeight - inputElPosition.bottom;
        if (availableHeight > optionsDropdownElHeight) {
          optionsDropdownEl.style.top = `${inputElPosition.top + inputElHeight}px`;
        } else {
          optionsDropdownEl.style.top = `${inputElPosition.top - optionsDropdownElHeight}px`;
        }
      }
    },
    getOptionSubLabel(option: StudioAdvancedSelectOption) {
      if (this.showOptionValueAsSubLabel) {
        return option.subLabel || option.value;
      }
      return option.subLabel;
    }
  }
});
</script>

<style lang="scss">
.studio-advanced-select {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 2.5rem;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
</style>
